import React, { useCallback, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";

import { Recipe, recipesApi } from "@/client/api";
import { Badge } from "@/client/components/ui/index";
import {
  ChatIcon,
  IngredientIcon,
  PlanIcon,
  RecipeIcon,
  SessionIcon,
  StockIcon,
} from "@/client/icons";
import { normalize } from "@/client/utils";

interface PaletteItem {
  id: string;
  label: string;
  hint: string;
  path: string;
  icon: React.ReactNode;
  badge?: string;
}

const SECTIONS: PaletteItem[] = [
  {
    id: "nav:chat",
    label: "Chat",
    hint: "Sección",
    path: "/",
    icon: <ChatIcon width={16} height={16} />,
  },
  {
    id: "nav:recetas",
    label: "Recetas",
    hint: "Sección",
    path: "/recetas",
    icon: <RecipeIcon width={16} height={16} />,
  },
  {
    id: "nav:ingredientes",
    label: "Ingredientes",
    hint: "Sección",
    path: "/ingredientes",
    icon: <IngredientIcon width={16} height={16} />,
  },
  {
    id: "nav:plan",
    label: "Plan semanal",
    hint: "Sección",
    path: "/plan",
    icon: <PlanIcon width={16} height={16} />,
  },
  {
    id: "nav:stock",
    label: "Stock",
    hint: "Sección",
    path: "/stock",
    icon: <StockIcon width={16} height={16} />,
  },
  {
    id: "nav:sesiones",
    label: "Sesiones",
    hint: "Sección",
    path: "/sesiones",
    icon: <SessionIcon width={16} height={16} />,
  },
];

export function CommandPalette({
  open,
  onClose,
  onNavigate,
}: {
  open: boolean;
  onClose: () => void;
  onNavigate: (path: string) => void;
}) {
  const [search, setSearch] = useState("");
  const [rawHighlightIdx, setHighlightIdx] = useState(0);
  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  // Fetch recipes once on first open
  const fetchedRef = useRef(false);
  useEffect(() => {
    if (open && !fetchedRef.current) {
      fetchedRef.current = true;
      void recipesApi.list().then(setRecipes);
    }
  }, [open]);

  // Reset state and focus on open
  useEffect(() => {
    if (open) {
      setSearch(""); // eslint-disable-line react-hooks/set-state-in-effect -- reset on open transition
      setHighlightIdx(0);
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  // Build filtered results
  const buildResults = useCallback((): PaletteItem[] => {
    const q = normalize(search);
    const sections = q ? SECTIONS.filter((s) => normalize(s.label).includes(q)) : SECTIONS;
    const recipeItems: PaletteItem[] = recipes
      .filter((r) => (q ? normalize(r.name).includes(q) : false))
      .slice(0, 8)
      .map((r) => ({
        id: `recipe:${r.id}`,
        label: r.name,
        hint: "Receta",
        path: `/recetas/${r.id}`,
        icon: <RecipeIcon width={16} height={16} />,
        badge: r.type ?? undefined,
      }));
    return [...sections, ...recipeItems];
  }, [search, recipes]);

  const results = buildResults();

  // Clamp highlight to valid range
  const highlightIdx = Math.min(rawHighlightIdx, Math.max(results.length - 1, 0));

  function handleSelect(item: PaletteItem) {
    onNavigate(item.path);
    onClose();
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightIdx((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (results[highlightIdx]) handleSelect(results[highlightIdx]);
    } else if (e.key === "Escape") {
      onClose();
    }
  }

  if (!open) return null;

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[70] flex items-start justify-center pt-[15vh] p-4">
      <div className="absolute inset-0 bg-black/40 animate-[fade-in_0.1s_ease]" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-term-surface border border-term-border rounded-xl shadow-2xl flex flex-col overflow-hidden animate-[fade-in-up_0.15s_ease_both]">
        {/* Search input */}
        <div className="px-5 py-4 border-b border-term-border">
          <input
            ref={inputRef}
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setHighlightIdx(0);
            }}
            onKeyDown={handleKeyDown}
            placeholder="Ir a sección o buscar receta..."
            className="w-full text-base bg-transparent text-term-text placeholder:text-term-text-muted focus:outline-none"
          />
        </div>

        {/* Results */}
        <div className="max-h-72 overflow-y-auto py-1 select-none">
          {results.length === 0 && (
            <p className="text-center text-base text-term-text-muted py-6">Sin resultados</p>
          )}
          {results.map((item, i) => (
            <button
              key={item.id}
              ref={
                i === highlightIdx ? (el) => el?.scrollIntoView({ block: "nearest" }) : undefined
              }
              onClick={() => handleSelect(item)}
              onMouseEnter={() => setHighlightIdx(i)}
              className={`w-full flex items-center gap-3 px-5 py-3 text-left text-base cursor-pointer transition-colors ${
                i === highlightIdx ? "bg-term-raised text-term-text" : "text-term-text-dim"
              }`}
            >
              <span className="shrink-0 text-term-text-muted">{item.icon}</span>
              <span className="flex-1 truncate">{item.label}</span>
              {item.badge && <Badge value={item.badge} />}
              <span className="text-micro text-term-text-muted shrink-0">{item.hint}</span>
            </button>
          ))}
        </div>
      </div>
    </div>,
    document.body,
  );
}
